import React, { useState } from "react";
import { ArrowUp, Plus, Trash2, X } from "lucide-react";
import { Branch, DepartmentMapping } from "../../api/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Pill } from "../ui/pill";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { CHANGED_RING, FieldRow, FormSection } from "./fields";
import { cn } from "@/lib/utils";

// Which department(s) get suggested when a vacature is created with this user
// as contactpersoon (or creator): a default list for every branch, plus
// per-branch lists that replace the default for that branch.
export interface DepartmentDraft {
  defaults: string[];
  overrides: { branchId: number; departments: string[] }[];
}

export function departmentDraftFromMappings(mappings: DepartmentMapping[]): DepartmentDraft {
  const byBranch = new Map<number, string[]>();
  const defaults: string[] = [];
  for (const m of mappings) {
    if (m.branch_id == null) defaults.push(m.department);
    else byBranch.set(m.branch_id, [...(byBranch.get(m.branch_id) ?? []), m.department]);
  }
  return { defaults, overrides: Array.from(byBranch, ([branchId, departments]) => ({ branchId, departments })) };
}

// An override with no departments is dropped, so that branch falls back to
// the defaults again.
export function departmentMappingsFromDraft(d: DepartmentDraft): DepartmentMapping[] {
  return [
    ...d.defaults.map(department => ({ branch_id: null, department })),
    ...d.overrides.flatMap(o => o.departments.map(department => ({ branch_id: o.branchId, department }))),
  ];
}

const key = (d: DepartmentDraft) => JSON.stringify(departmentMappingsFromDraft(d));
export const departmentsChanged = (cur: DepartmentDraft, orig: DepartmentDraft) => key(cur) !== key(orig);

interface Props {
  cur: DepartmentDraft;
  orig: DepartmentDraft;
  branches: Branch[];
  onChange: (next: DepartmentDraft) => void;
}

export function UserDepartmentsTab({ cur, orig, branches, onChange }: Props) {
  const branchName = (id: number) => branches.find(b => b.id === id)?.name ?? `Vestiging #${id}`;
  const available = branches.filter(b => !cur.overrides.some(o => o.branchId === b.id));
  const origFor = (branchId: number) => orig.overrides.find(o => o.branchId === branchId)?.departments ?? [];
  const setOverride = (branchId: number, departments: string[]) =>
    onChange({ ...cur, overrides: cur.overrides.map(o => o.branchId === branchId ? { ...o, departments } : o) });

  return (
    <div className="flex flex-col gap-7">
      <FormSection title="Voorgestelde afdeling">
        <p className="pb-2 text-sm text-waves-muted text-pretty">
          Bij een nieuwe vacature wordt de eerste afdeling voorgeselecteerd. Een vestiging met eigen afdelingen gebruikt de standaard niet.
        </p>
        <FieldRow label="Standaard" hint="Alle vestigingen">
          <DepartmentList
            label="Standaard"
            departments={cur.defaults}
            changed={JSON.stringify(cur.defaults) !== JSON.stringify(orig.defaults)}
            onChange={defaults => onChange({ ...cur, defaults })}
          />
        </FieldRow>
        {cur.overrides.map(o => (
          <FieldRow
            key={o.branchId}
            label={branchName(o.branchId)}
            hint="Eigen afdelingen"
            labelExtra={
              <button
                type="button"
                onClick={() => onChange({ ...cur, overrides: cur.overrides.filter(x => x.branchId !== o.branchId) })}
                className="mt-1 inline-flex items-center gap-1 self-start text-[13px] font-bold text-status-alert-fg hover:underline [&_svg]:size-3.5"
              >
                <Trash2 /> Verwijderen
              </button>
            }
          >
            <DepartmentList
              label={branchName(o.branchId)}
              departments={o.departments}
              changed={JSON.stringify(o.departments) !== JSON.stringify(origFor(o.branchId))}
              onChange={next => setOverride(o.branchId, next)}
            />
          </FieldRow>
        ))}
        {available.length > 0 && (
          <FieldRow label="Uitzondering" hint="Andere afdelingen voor één vestiging">
            <Select value="" onValueChange={v => onChange({ ...cur, overrides: [...cur.overrides, { branchId: Number(v), departments: [] }] })}>
              <SelectTrigger aria-label="Uitzondering voor vestiging toevoegen" className="max-w-[360px] text-waves-muted">
                <SelectValue placeholder="+ Vestiging kiezen…" />
              </SelectTrigger>
              <SelectContent>
                {available.map(b => <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </FieldRow>
        )}
      </FormSection>
    </div>
  );
}

// One ordered list of departments. The first entry is the one preselected on
// a new vacature, so order matters and can be changed with the move-up button.
function DepartmentList({ label, departments, changed, onChange }: {
  label: string; departments: string[]; changed: boolean; onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("");

  function add() {
    const value = draft.trim();
    if (!value) return;
    if (!departments.includes(value)) onChange([...departments, value]);
    setDraft("");
  }

  function moveUp(i: number) {
    const next = [...departments];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  }

  return (
    <div className={cn("flex flex-col gap-1 rounded-nav border border-waves-line p-2", changed && CHANGED_RING)}>
      {departments.map((d, i) => (
        <div key={d} className="flex items-center justify-between gap-2 rounded-nav px-2 py-1.5 text-[15px] text-waves-ink hover:bg-waves-subtle">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate">{d}</span>
            {i === 0 && departments.length > 1 && <Pill kind="open">voorgeselecteerd</Pill>}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {i > 0 && (
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`${d} omhoog`} onClick={() => moveUp(i)}>
                <ArrowUp />
              </Button>
            )}
            <Button
              variant="ghost" size="icon"
              className="h-8 w-8 text-status-alert-fg hover:bg-status-alert-bg"
              aria-label={`${d} verwijderen`}
              onClick={() => onChange(departments.filter(x => x !== d))}
            >
              <X />
            </Button>
          </span>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <Input
          value={draft}
          placeholder="Afdeling toevoegen…"
          aria-label={`Afdeling toevoegen aan ${label}`}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        />
        <Button variant="outline" size="sm" onClick={add} disabled={!draft.trim()}>
          <Plus /> Toevoegen
        </Button>
      </div>
    </div>
  );
}
